// Wrapper léger autour de fetch — credentials same-origin par défaut + parsing JSON
// + erreurs typées.

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const r = await fetch(input, { credentials: "same-origin", ...init });
  if (!r.ok) {
    let body: unknown = null;
    try {
      body = await r.json();
    } catch {
      /* ignore */
    }
    const msg = (body as { error?: string })?.error || `HTTP ${r.status}`;
    throw new ApiError(r.status, msg, body);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// PO-81 — version du service (package.json + commit git court), rendue par
// /api/health même base KO (500, corps conservé dans ApiError.body).
export interface BuildInfo {
  version: string;
  commit: string;
}

export async function fetchBuildInfo(): Promise<BuildInfo | null> {
  const pick = (b: unknown): BuildInfo | null => {
    const o = b as Partial<BuildInfo> | null;
    return o && typeof o.version === "string" && typeof o.commit === "string"
      ? { version: o.version, commit: o.commit }
      : null;
  };
  try {
    return pick(await api<unknown>("/api/health"));
  } catch (e) {
    return e instanceof ApiError ? pick(e.body) : null;
  }
}

// Shapes (souples — les API existantes renvoient pas mal de variantes)
export interface CardSummary {
  id: string;
  name?: string | null;
  set_name?: string | null;
  set_code?: string | null;
  number?: string | null;
  rarity?: string | null;
  price?: number | string | null;
  asked_price?: number | string | null;
  language?: string | null;
  // PO-54 : marque du prix de carte (raw | history_only | current_only | stale | fallback_no_raw | null).
  price_basis?: "raw" | "history_only" | "current_only" | "stale" | "fallback_no_raw" | null;
  raw_price_count?: number | null;
  // PO-74 : exact = mots trouvés (sans accents ni casse) ; approx = nom proche (trigrammes).
  match?: "exact" | "approx";
  similarity?: number | null;
}

export interface CardsListResp {
  items: CardSummary[];
  page?: number;
  totalPages?: number;
  total?: number;
  exact_total?: number;
  approx_count?: number;
}

// Carrousel d'accueil — cartes récemment mises à jour (prix + date de MAJ).
export interface RecentCard {
  id: string;
  name?: string | null;
  rarity?: string | null;
  set_name?: string | null;
  image_url?: string | null;
  price?: number | string | null;
  currency?: string | null;
  price_last_update?: string | null;
  // PO-54 : marque du prix de carte (raw | history_only | current_only | stale | fallback_no_raw | null).
  price_basis?: "raw" | "history_only" | "current_only" | "stale" | "fallback_no_raw" | null;
  raw_price_count?: number | null;
}

export interface RecentCardsResp {
  items: RecentCard[];
}

// Stats globales (/api/stats/full) — couverture + fraîcheur + sources + santé.
export interface FullStats {
  generated_at: string;
  health: { db: string };
  cards: { total: number; with_card_price: number };
  coverage: { total_cards: number; priced_cards: number };
  freshness: {
    total: number;
    fresh_2d: number;
    fresh_7d: number;
    stale_7d: number;
    stale_30d: number;
    oldest: string | null;
    newest: string | null;
  };
  metadata: { priced: number; priced_with_image: number; with_metadata: number };
  data_quality: { stat_rows: number; total_rows: number };
  by_language: { language: string; cards: number; priced: number; fresh_7d: number }[];
  by_source: {
    source: string;
    observations: number;
    cards: number;
    fresh_2d: number;
    stale_7d: number;
    last_seen: string | null;
  }[];
  last_runs: {
    source_key: string;
    started_at: string;
    success: boolean;
    exit_code: number | null;
    value_upserts: number | null;
    duration_s: number | null;
  }[];
  job?: {
    running?: boolean;
    currentScope?: string | null;
    currentFilter?: string | null;
    lastScope?: string | null;
    lastRun?: string | null;
    lastExitCode?: number | null;
  };
}

export interface PriceRow {
  source?: string | null;
  version?: string | null;
  grading_co?: string | null;
  grade?: string | null;
  price?: number | string | null;
  currency?: string | null;
  condition?: string | null;
  language?: string | null;
  created_at?: string | null;
  // PO-65 : dernière fois vue (relecture identique sans nouvelle ligne). NULL ⇒ created_at.
  last_seen_at?: string | null;
  price_date?: string | null;
  raw_data?: Record<string, unknown> | null;
  // PO-73 : lien vers la source, nettoyé côté serveur (https, hôte public). exact=true :
  // price_details.source_url (eBay, extensions). exact=false : lien par carte de
  // card_metadata (CardMarket / TCGPlayer / TCGGo). null : source sans lien.
  source_url?: string | null;
  source_url_exact?: boolean;
}

// Carnet d'ordres par état : "RAW" = carte loose (non gradée), condition
// renseignée (échelle CardMarket/eBay) ; sinon carte gradée (PSA/Collectaura),
// grade renseigné. Un seul des deux couples (grade|cert_number) / (condition)
// est rempli selon le grader — jamais les deux.
export type Grader = "PSA" | "Collectaura" | "RAW";
export type LooseCondition = "MT" | "NM" | "EX" | "GD" | "LP" | "PL" | "PO";

export interface ListingRow {
  id?: number;
  card_id: string;
  card_name?: string | null;
  grader: string;
  grade?: string | number | null;
  cert_number?: string | null;
  condition?: LooseCondition | null;
  price_usdc: number | string;
  seller_wallet: string;
  created_at: string;
}

export interface WTBRow {
  id?: number;
  card_id: string;
  card_name?: string | null;
  grader: string;
  grade?: string | number | null;
  condition?: LooseCondition | null;
  price_usdc: number | string;
  buyer_wallet: string;
  onchain_status?: "created" | "claimed" | "released" | "refunded" | null;
  created_at: string;
}

// Grille BUY/SELL par état — une ligne = un état (loose ou gradée) avec
// quantité + prix. Soumis à /api/wtb/batch ou /api/listings/batch.
export interface BatchOrderRow {
  grader: Grader;
  condition?: LooseCondition;
  grade?: string;
  price_usdc: number;
  quantity: number;
}

export interface BatchOrderRequest {
  card_id: string;
  rows: BatchOrderRow[];
}

// Top movers
export interface MoverRow {
  rank: number;
  card_id: string;
  card_name?: string | null;
  start_day: string;
  end_day: string;
  start_price_eur: number;
  end_price_eur: number;
  abs_change_eur: number;
  pct_change: number;
  median_price_eur: number;
  distinct_days: number;
  total_points: number;
}

// Favorites
export interface FavoriteRow {
  card_id: string;
  card_name?: string | null;
  created_at: string;
}

// "Me" — listings/wtb perso
export interface MyListingRow extends ListingRow {
  status: string;
}

export interface MyWTBRow extends WTBRow {
  status: string;
}

// /api/auth/me payload
export interface MeProfile {
  wallet: string | null;
  wallet_checksum: string | null;
  profile?: {
    created_at?: string | null;
    last_login_at?: string | null;
  };
}

// Desirability (PR-229)
export interface DesirabilityRow {
  species_id: number;
  rank: number;
  avg_price_eur: string | number;
  n_cards_contributing: number;
  rank_delta_vs_prev_day: number | null;
  english?: string | null;
  french?: string | null;
  japanese?: string | null;
  italian?: string | null;
  spanish?: string | null;
  korean?: string | null;
  german?: string | null;
  simplified_chinese?: string | null;
  traditional_chinese?: string | null;
}

export interface DesirabilityRankingResp {
  snapshot_date: string | null;
  language: string;
  total: number;
  limit?: number;
  offset?: number;
  rows: DesirabilityRow[];
}

export interface DesirabilityLanguagesResp {
  languages: { language: string; n_species: number }[];
}

export interface DesirabilityHistoryPoint {
  snapshot_date: string;
  avg_price_eur: string | number;
  n_cards_contributing: number;
  rank: number;
  rank_delta_vs_prev_day: number | null;
  language: string;
}

export interface DesirabilityHistoryResp {
  species: DesirabilityRow | null;
  language: string;
  history: DesirabilityHistoryPoint[];
}

export interface DesirabilityCardRow {
  card_id: string;
  name?: string | null;
  language?: string | null;
  extension?: string | null;
  setcode?: string | null;
  number?: string | null;
  rarity?: string | null;
  is_tag_team_part: boolean;
  matched_via: string;
  average_price?: string | number | null;
  average_30d?: string | number | null;
  average_7d?: string | number | null;
  median_price?: string | number | null;
  price_count?: number | null;
}

// PR-1380 — entrées manuelles (carte/scellé), PR-1377
export interface ManualEntry {
  id: number | string;
  kind: "card" | "sealed";
  card_id?: string | null;
  name: string;
  language?: string | null;
  extension_name?: string | null;
  number?: string | null;
  product_type?: string | null;
  condition?: string | null;
  price?: number | string | null;
  currency?: string | null;
  image_s3_key: string;
  image_s3_url: string;
  source_url?: string | null;
  created_via: string;
  created_at: string;
}
export interface ManualEntryProductType {
  code: string;
  label_fr: string;
  sort_order: number;
}

// PSA / grader lookup
export interface GraderLookupResp {
  parsed: {
    cert_number: string;
    year?: string | null;
    brand?: string | null;
    category?: string | null;
    variety?: string | null;
    card_number?: string | null;
    subject?: string | null;
    grade?: string | null;
    population_at_grade?: number | null;
    population_higher?: number | null;
  };
  grader_set_key?: string | null;
  cached_extension?: string | null;
  suggested_query?: string | null;
}
